import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Lock, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!token) {
      toast.error("Missing reset token");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-grid flex flex-col items-center justify-center p-6">
      <button data-testid="reset-back-login" onClick={() => navigate("/login")} className="absolute top-6 left-6 flex items-center gap-2 text-sm text-gray-400 hover:text-amber-400 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to sign in
      </button>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md bg-card border border-amber-500/20 rounded-2xl p-8 sm:p-10 shadow-[0_0_60px_rgba(245,158,11,0.06)]">
        <div className="flex items-center gap-3 mb-6">
          <img src="/streamworship-mark.png" alt="StreamWorship" className="h-11 w-11 object-contain" />
          <p className="font-display font-bold text-lg">Stream<span className="text-gold-gradient">Worship</span></p>
        </div>

        {done ? (
          <div className="text-center py-4">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-4">Password updated</h1>
            <p className="text-gray-400 text-sm mt-2 mb-6">You can now sign in with your new password.</p>
            <button data-testid="reset-go-login" onClick={() => navigate("/login")} className="w-full gold-gradient hover:brightness-110 text-black font-semibold py-3.5 rounded-xl transition-all active:scale-[0.98]">
              Go to sign in
            </button>
          </div>
        ) : !token ? (
          <div className="text-center py-4">
            <h1 className="font-display text-2xl font-bold">Invalid reset link</h1>
            <p className="text-gray-400 text-sm mt-2 mb-6">This link is missing its token. Please request a new reset link.</p>
            <button data-testid="reset-request-new" onClick={() => navigate("/forgot-password")} className="text-amber-400 hover:underline text-sm">Request a new link</button>
          </div>
        ) : (
          <>
            <h1 className="font-display text-2xl font-bold">Set a new password</h1>
            <p className="text-gray-400 text-sm mt-2 mb-6">Choose a strong password for your account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">New password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="pl-10 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Confirm password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                  <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter your password" className="pl-10 bg-gray-950 border-gray-800 focus-visible:ring-amber-500/40 h-11" />
                </div>
              </div>
              <button data-testid="reset-submit" type="submit" disabled={submitting} className="w-full gold-gradient hover:brightness-110 text-black font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 transition-all active:scale-[0.98] disabled:opacity-60">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {submitting ? "Updating…" : "Update password"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
